import {
  ContractApprovalProgressStatus,
  GetApprovalContractsFilter,
  useContractsGetApprovalContractsSuspense,
} from "@/orval/gen"
import { Page } from "@components/Page"
import {
  Table,
  Paper,
  Group,
  Select,
  Text,
  BadgeProps,
  Badge,
} from "@mantine/core"
import { createFileRoute } from "@tanstack/react-router"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useTranslation } from "react-i18next"
import { useGetFormattedDatePeriod } from "@/utils/use-get-formatted-date-period"
import { ScrollContainer } from "@components/scroll-container"
import { match } from "ts-pattern"

export const Route = createFileRoute("/app/contracts/approvals/")({
  component: RouteComponent,
})

function RouteComponent() {
  const [filter, setFiler] = useUserLocalStorage<GetApprovalContractsFilter>({
    key: "contract-approvals-filter",
    defaultValue: GetApprovalContractsFilter.ToDo,
  })
  const { data } = useContractsGetApprovalContractsSuspense({ request: filter })
  const { t } = useTranslation("app_server")
  const navigate = Route.useNavigate()
  const getFormattedDatePeriod = useGetFormattedDatePeriod()

  const Td = React.memo(({ children }: { children: React.ReactNode }) => (
    <Table.Td maw={150}>
      <Text lineClamp={1} truncate>
        {children}
      </Text>
    </Table.Td>
  ))

  const rows = data.map((c) => (
    <Table.Tr
      style={{
        cursor: "pointer",
      }}
      onClick={() =>
        navigate({
          to: "$contractId",
          params: { contractId: c.conractId.toString() },
        })
      }
      key={c.conractId}
    >
      <Td>
        <ProgressStatusBadge value={c.progressStatus} />
      </Td>
      <Td>{c.status?.name}</Td>
      <Td>{c.personFullName}</Td>
      <Td>{c.contractType.name}</Td>
      <Td>{getFormattedDatePeriod(c.startDate, c.endDate)}</Td>
      <Td>{c.productionRoles.join(", ")}</Td>
    </Table.Tr>
  ))

  return (
    <Page>
      <Group my={"md"}>
        <Select
          data={[
            GetApprovalContractsFilter.All,
            GetApprovalContractsFilter.InProgress,
            GetApprovalContractsFilter.ToDo,
          ].map((v) => ({ value: v, label: t(v) }))}
          value={filter}
          onChange={(v) => v && setFiler(v as GetApprovalContractsFilter)}
        />
      </Group>
      <Paper withBorder shadow="xs">
        <ScrollContainer h={"100%"}>
          <Table stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={125}>{t("Status")}</Table.Th>
                <Table.Th w={125}></Table.Th>
                <Table.Th w={200}>{t("Person")}</Table.Th>
                <Table.Th w={125}>{t("ContractType")}</Table.Th>
                <Table.Th w={190}>{t("TimePeriod")}</Table.Th>
                <Table.Th>{t("Production")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollContainer>
      </Paper>
    </Page>
  )
}

function ProgressStatusBadge({
  value,
}: {
  value: ContractApprovalProgressStatus
}) {
  const { t } = useTranslation("app_server")
  const props = match<ContractApprovalProgressStatus, BadgeProps>(value)
    .with("Approved", () => ({ color: "green" }))
    .with("InProgress", () => ({ color: "blue" }))
    .with("ToDo", () => ({ color: "pink" }))
    .exhaustive()

  return <Badge variant="light" size="sm" {...props} children={t(value)} />
}
